"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppAccessReviewPageDetailSchema = exports.appAccessReviewPageNodeId = exports.APP_ACCESS_REVIEW_NODE_ID = exports.AppAccessReviewSchema = exports.AppAccessReviewPageSchema = exports.AppAccessTableAudiencesSchema = exports.AppAccessAudiencesSchema = exports.appAccessTableTargetId = exports.appAccessPageTargetId = exports.APP_ACCESS_STAGE_TARGETS = void 0;
exports.appAccessReview = appAccessReview;
exports.appAccessEditsPage = appAccessEditsPage;
exports.readAppAccessReviewPage = readAppAccessReviewPage;
exports.withAppAccessReviewNode = withAppAccessReviewNode;
/**
 * The "Data & access" station of an App goal: what each page grants now and
 * after the App's recommendations, and the Studio targets the Copilot leads
 * the user to. The goal run on the server, the Studio navigation targets and
 * the goal panel read this one contract. It carries rights, never wording:
 * the client says them in the viewer's language.
 */
const zod_1 = require("zod");
const app_access_vocabulary_1 = require("./app-access-vocabulary");
const app_base_contract_1 = require("./app-base-contract");
const app_page_access_1 = require("./app-page-access");
/**
 * Navigation owners and IDs of the access stages in the App Studio: the rail
 * entry opens the advisor, a page target shows one page with its editor, a
 * table target opens one table of that page.
 */
exports.APP_ACCESS_STAGE_TARGETS = {
    rail: { ownerId: 'launcher-app-base', id: 'access' },
    pagesOwnerId: 'app-studio-access-pages',
    tablesOwnerId: 'app-studio-access-tables',
};
/** Page target, addressed by the page path of `?route=`. */
const appAccessPageTargetId = (routePath) => JSON.stringify(['page', routePath]);
exports.appAccessPageTargetId = appAccessPageTargetId;
/** Table target, addressed by the source key of `?table=`. */
const appAccessTableTargetId = (routePath, sourceKey) => JSON.stringify(['table', routePath, sourceKey]);
exports.appAccessTableTargetId = appAccessTableTargetId;
/** What each audience may do on a page; absent audiences reach nothing. */
exports.AppAccessAudiencesSchema = zod_1.z
    .object(Object.fromEntries(app_access_vocabulary_1.AppAccessAudienceSchema.options.map(audience => [
    audience,
    app_access_vocabulary_1.AppAudienceAccessSchema.optional(),
])))
    .strict();
/** The own rights of one table per audience; absent audiences follow the page. */
exports.AppAccessTableAudiencesSchema = zod_1.z
    .object(Object.fromEntries(app_access_vocabulary_1.AppAccessAudienceSchema.options.map(audience => [
    audience,
    app_access_vocabulary_1.AppTableAudienceAccessSchema.optional(),
])))
    .strict();
exports.AppAccessReviewPageSchema = zod_1.z
    .object({
    routePath: zod_1.z.string().min(1),
    title: zod_1.z.string(),
    targetId: zod_1.z.string().min(1),
    current: exports.AppAccessAudiencesSchema,
    /** After "Apply all": the recommendation, else what the page has. */
    recommended: exports.AppAccessAudiencesSchema,
    /** After "Apply all", per table the App brings rights for: its own ones. */
    recommendedTables: zod_1.z.record(zod_1.z.string().min(1), exports.AppAccessTableAudiencesSchema),
})
    .strict();
exports.AppAccessReviewSchema = zod_1.z
    .object({
    pages: zod_1.z.array(exports.AppAccessReviewPageSchema),
    /** The edits "Apply all" writes, as the Studio button writes them. */
    recommendedEdits: zod_1.z.array(app_page_access_1.AppAccessEditSchema),
})
    .strict();
function audiencesOf(section, routePath) {
    return Object.fromEntries(app_access_vocabulary_1.AppAccessAudienceSchema.options.flatMap(audience => {
        const access = (0, app_page_access_1.appPageAudienceAccess)(section, routePath, audience);
        return access ? [[audience, access]] : [];
    }));
}
function tableAudiencesOf(section, routePath, sourceKey) {
    return Object.fromEntries(app_access_vocabulary_1.AppAccessAudienceSchema.options.flatMap(audience => {
        const setting = (0, app_page_access_1.appAccessSetting)(section, routePath, audience, sourceKey);
        const access = setting.from === 'table' ? setting.access : undefined;
        return access ? [[audience, access]] : [];
    }));
}
function appAccessReview(app) {
    const recommendedEdits = (0, app_page_access_1.appAccessRecommendationEdits)(app.routes);
    const recommendedSection = recommendedEdits.reduce((section, edit) => (0, app_page_access_1.applyAppAccessEdit)(section, edit), app.pageAccess);
    return {
        pages: app.routes.map(route => ({
            routePath: route.path,
            title: route.title,
            targetId: (0, exports.appAccessPageTargetId)(route.path),
            current: audiencesOf(app.pageAccess, route.path),
            recommended: audiencesOf(recommendedSection, route.path),
            recommendedTables: Object.fromEntries(Object.keys(route.access?.tables ?? {}).map(sourceKey => [
                sourceKey,
                tableAudiencesOf(recommendedSection, route.path, sourceKey),
            ])),
        })),
        recommendedEdits,
    };
}
/**
 * The page all edits change, or null when they change the defaults or more
 * than one page. Adjusting at the station writes one page at a time.
 */
function appAccessEditsPage(edits) {
    const paths = new Set(edits.map(edit => edit.kind === 'defaults' || edit.routePath === undefined
        ? null
        : (0, app_base_contract_1.normalizeAppRoutePath)(edit.routePath)));
    if (paths.size !== 1)
        return null;
    const [path] = paths;
    return path ?? null;
}
/** The goal panel step "Data & access" and its pages. */
exports.APP_ACCESS_REVIEW_NODE_ID = 'appBase.access.review';
const appAccessReviewPageNodeId = (routePath) => `${exports.APP_ACCESS_REVIEW_NODE_ID}:${routePath}`;
exports.appAccessReviewPageNodeId = appAccessReviewPageNodeId;
/** What a page step of the station carries: its rights after "Apply all". */
exports.AppAccessReviewPageDetailSchema = zod_1.z
    .object({
    routePath: zod_1.z.string().min(1),
    recommended: exports.AppAccessAudiencesSchema,
})
    .strict();
/** The detail of a page step of the station; null for any other node. */
function readAppAccessReviewPage(node) {
    const parsed = exports.AppAccessReviewPageDetailSchema.safeParse(node.detail);
    return parsed.success &&
        node.nodeId === (0, exports.appAccessReviewPageNodeId)(parsed.data.routePath)
        ? parsed.data
        : null;
}
function progressWith(progress, status, delta) {
    return status === 'completed'
        ? {
            ...progress,
            completed: (progress.completed ?? 0) + delta,
            total: progress.total + delta,
        }
        : {
            ...progress,
            blocked: progress.blocked + delta,
            total: progress.total + delta,
        };
}
function isReviewNode(node) {
    return node.nodeId === exports.APP_ACCESS_REVIEW_NODE_ID;
}
/**
 * The tree with the station as the last step of its root, replacing an
 * earlier one. The station counts as one step; its pages only show what
 * each page grants after "Apply all".
 */
function withAppAccessReviewNode(tree, review, status) {
    const earlier = tree.root.children.find(isReviewNode);
    const node = {
        nodeId: exports.APP_ACCESS_REVIEW_NODE_ID,
        kind: 'exposure',
        label: 'Data & access',
        status,
        required: true,
        children: review.pages.map(page => ({
            nodeId: (0, exports.appAccessReviewPageNodeId)(page.routePath),
            kind: 'route',
            label: page.title || page.routePath,
            status,
            required: false,
            detail: {
                routePath: page.routePath,
                recommended: page.recommended,
            },
            children: [],
        })),
    };
    const progress = earlier
        ? progressWith(progressWith(tree.progress, earlier.status, -1), status, 1)
        : progressWith(tree.progress, status, 1);
    return {
        ...tree,
        root: {
            ...tree.root,
            children: [
                ...tree.root.children.filter(child => !isReviewNode(child)),
                node,
            ],
        },
        progress,
    };
}
